import clsx from "clsx"
import { type CSSProperties, type ReactNode, type ThHTMLAttributes, useMemo } from "react"
import { TypeCheck } from "@/shared/utils"
import Spinner from "../Spinner.tsx"
import "./style.scss"
import { useTranslation } from "react-i18next"
import { tCommon, tTable } from "@/shared/config/i18n"
import { cn } from "@/shared/lib/utils.ts"
import type { ILocaleLeafKeysTable, ILocaleLeafKeysTableHeaders } from "@/shared/types/i18n-resource-keys.ts"
import { TableFetchingEffect } from "@/shared/ui/table-fetching-effect.tsx"

export interface ITableHeaderBase {
  rowSpan?: number
  colSpan?: number
  className?: string
  style?: CSSProperties
}

export interface ITableHeaderLocale extends ITableHeaderBase {
  labelLocale: ILocaleLeafKeysTableHeaders
}

export interface ITableHeaderLabel extends ITableHeaderBase {
  label: ReactNode
}

export const isITableHeaderLocale = (header: ITableHeader): header is ITableHeaderLocale =>
  TypeCheck.isObject(header) && "labelLocale" in header && typeof header.labelLocale === "string"

export type ITableHeader = ITableHeaderLocale | ITableHeaderLabel

interface ITableProps {
  headers: (ITableHeader | ITableHeader[])[]
  data: ReactNode[][]
  loading?: boolean
  className?: string
  isFetching?: boolean
  showHeader?: boolean
  thProps?: ThHTMLAttributes<HTMLTableCellElement>
}

export const Table = ({ headers, thProps, isFetching, data, loading = false, showHeader = true, className }: ITableProps) => {
  const { t, i18n } = useTranslation("table")

  const translatedHeaders = useMemo(() => {
    return headers.map(row =>
      Array.isArray(row)
        ? row.map(h => ({
            ...h,
            label: isITableHeaderLocale(h) ? t(`headers.${h.labelLocale}`, h.labelLocale) : h.label
          }))
        : {
            ...row,
            label: isITableHeaderLocale(row) ? t(`headers.${row.labelLocale}`, row.labelLocale) : row.label
          }
    )
  }, [headers, i18n.language])

  return (
    <table className={clsx("base-table", className)}>
      {showHeader && (
        <thead className="z-[200]">
          {translatedHeaders.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {Array.isArray(row) ? (
                row.map((header, index) => (
                  <th
                    key={index}
                    rowSpan={header.rowSpan}
                    colSpan={header.colSpan}
                    style={header.style}
                    {...thProps}
                    className={cn(header.className, thProps?.className)}
                  >
                    {tTable(`headers.${header.label}` as ILocaleLeafKeysTable, typeof header.label === "string" ? header.label : "")}
                  </th>
                ))
              ) : (
                <th key={rowIndex} rowSpan={row.rowSpan} colSpan={row.colSpan} style={row.style} className={row.className}>
                  {row.label}
                </th>
              )}
            </tr>
          ))}
        </thead>
      )}

      <tbody className="relative">
        <TableFetchingEffect show={!loading && isFetching} />
        {loading ? (
          <tr>
            <td
              colSpan={
                Array.isArray(headers?.[0]) ? headers[0].length + (Array.isArray(headers?.[1]) ? headers?.[1]?.length : 0) : headers?.length || 0
              }
            >
              <Spinner loaderProps={{ size: 20 }} className="text-lg" />
            </td>
          </tr>
        ) : data.length === 0 ? (
          <tr>
            <td
              className="text-center"
              colSpan={
                Array.isArray(headers?.[0]) ? headers[0].length + (Array.isArray(headers?.[1]) ? headers?.[1]?.length : 0) : headers?.length || 0
              }
            >
              {tCommon("no_data")}
            </td>
          </tr>
        ) : (
          data.length > 0 &&
          data.map((row, i) => (
            <tr key={i}>
              {row.map((data, j) => {
                const isArray = TypeCheck.isArray(data)
                return (
                  <td key={j} className={clsx(isArray && "array-cell")}>
                    {isArray ? (
                      <div className="array-cell--inner">
                        {data.map((value, vi) => (
                          <div key={vi}>{value as ReactNode}</div>
                        ))}
                      </div>
                    ) : (
                      data
                    )}
                  </td>
                )
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}
